/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2026
///                                    RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment } from "react";
import { useSelector } from "react-redux";

import {
  userRole,
  status as bidStatus,
  mode,
  awardStatus as bidAwardStatus,
  repType,
  repriceReason as pricingReason,
  capacityMatchingStatus,
  bidTypes
} from "../../helpers/types";
import {
  shouldShowRejectedCloseBid,
  shouldShowSelfPricedEarlyCloseBid
} from "../../helpers/closeLostWithCrm";
import getModeForUserRole from "../../helpers/getModeForUserRole";
import SalesViewPendingBidState from "./banner/SalesViewPendingBidState";
import SalesSelfPricedOwnerPendingBidState from "./banner/SalesSelfPricedOwnerPendingBidState";
import SalesViewRejectedBidState from "./banner/SalesViewRejectedBidState";
import SalesViewArchivedBidState from "./banner/SalesViewArchivedBidState";
import PricingViewRejectedBidState from "./banner/PricingViewRejectedBidState";
import PricingViewPendingBidState from "./banner/PricingViewPendingBidState";
import UploadLanesState from "./banner/UploadLanesState";
import SubmitPricingState from "./banner/SubmitPricingState";
import SubmittedBidState from "./banner/SubmittedBidState";
import ImportAwardState from "./banner/ImportAwardState";
import UploadRatesState from "./banner/UploadRatesState";
import ImportProgressState from "./banner/ImportProgressState";
import LaneRepriceProgressState from "./banner/LaneRepriceProgressState";
import PendingProcurementReviewState from "./banner/PendingProcurementReviewState";
import messages from "../../helpers/messages";
import ProcurementViewPendingState from "./banner/ProcurementViewPendingState";
import ProcurementViewRejectedState from "./banner/ProcurementViewRejectedState";
import ProcurementViewInProgressState from "./banner/ProcurementViewInProgressState";
import CarrierRepViewInProgressState from "./banner/CarrierRepViewInProgressState";
import CarrierRepViewReviewState from "./banner/CarrierRepViewReviewState";
import CapacityMatchingProgressState from "./banner/CapacityMatchingProgressState";

export const BannerInfo = ({
  summary,
  user,
  hideBanner,
  isLaneImportInProgress,
  isLaneRepriceInProgress,
  isCapacityMatchingInProgress,
  repriceReason,
  enableInternalCapacityMatching
}) => {
  const selfPricedCloseLost = useSelector(
    state => state.featureFlags.selfPricedCloseLost
  );
  const showSelfPricedEarlyCloseBid = shouldShowSelfPricedEarlyCloseBid(
    summary,
    user,
    selfPricedCloseLost
  );
  const showRejectedCloseBid = shouldShowRejectedCloseBid(
    summary,
    user,
    selfPricedCloseLost
  );
  const { modes, bidId, bidTypeId } = summary;
  const { userId, division } = user;
  const role =
    user.role === userRole.management
      ? user.userRoleForManagementUser
      : user.role;

  const renderBannerInfo = () => {
    /* when lane import is in progress */
    if (isLaneImportInProgress) {
      return <ImportProgressState />;
    } else if (isLaneRepriceInProgress) {
      /* when lane re-price is in progress */
      const message =
        repriceReason === pricingReason.upload
          ? messages.laneInitialPricingBannerMessage.defaultMessage
          : messages.laneInRepriceProgressBannerMessage.defaultMessage;
      return <LaneRepriceProgressState message={message} />;
    }

    /* when capacity matching is in progress */
    if (isCapacityMatchingInProgress) {
      return <CapacityMatchingProgressState />;
    }

    if (role === userRole.salesRep) {
      /* when all the modes within the bid are in the Pending state and the user is not the bid owner */
      if (
        modes.every(
          x => x.status === bidStatus.pending && x.bidOwnerId !== userId
        )
      ) {
        return <SalesViewPendingBidState hideBanner={hideBanner} />;
      }

      if (
        selfPricedCloseLost &&
        modes.every(x => x.status === bidStatus.pending) &&
        modes.some(
          x => x.mode === mode.tl && x.bidOwnerRepType === repType.sales
        )
      ) {
        return (
          <SalesSelfPricedOwnerPendingBidState
            summary={summary}
            user={user}
            hideBanner={hideBanner}
          />
        );
      }

      /* when any of the mode within the bid is in Rejected state */
      if (modes.some(x => x.status === bidStatus.rejected)) {
        return (
          <SalesViewRejectedBidState
            summary={summary}
            division={division}
            role={role}
            user={user}
            showCloseBidInsteadOfArchive={showRejectedCloseBid}
          />
        );
      }

      /* when all the modes within the bid is in Closed state and IsArchived = true */
      if (
        modes.every(x => x.status === bidStatus.closed && x.isArchived === true)
      ) {
        return <SalesViewArchivedBidState />;
      }

      /* when TL-specific-mode within the bid is In Progress state and IsLaneUploadComplete = false */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === false &&
            x.bidOwnerRepType === repType.sales
        )
      ) {
        return (
          <UploadLanesState
            bidId={bidId}
            role={role}
            division={division}
            summary={summary}
            user={user}
            showSelfPricedEarlyCloseBid={showSelfPricedEarlyCloseBid}
          />
        );
      }

      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true*/
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.sales
        )
      ) {
        return (
          <SubmitPricingState
            summary={summary}
            division={division}
            role={role}
            user={user}
            showSelfPricedEarlyCloseBid={showSelfPricedEarlyCloseBid}
          />
        );
      }

      /* when all the modes within the bid is in Submitted state except for the modes that are rejected */
      const nonRejectedModes = modes.filter(x => x.status !== bidStatus.closed);
      if (
        nonRejectedModes.length &&
        nonRejectedModes.every(x => x.status === bidStatus.submitted)
      ) {
        return <SubmittedBidState summary={summary} user={user} />;
      }

      /* when TL-specific-mode within the bid is in Feedback state */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.feedback &&
            x.bidOwnerRepType === repType.sales
        )
      ) {
        const isAwardImportComplete = modes.find(
          x => x.mode === mode.tl
        ).isAwardImportComplete;
        return (
          <ImportAwardState
            bidId={bidId}
            user={user}
            isAwardImportComplete={isAwardImportComplete}
            summary={summary}
          />
        );
      }

      /* when TL-specific-mode within the bid is in Closed state and Award status is Won */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.closed &&
            x.awardStatus === bidAwardStatus.won &&
            x.bidOwnerRepType === repType.sales
        )
      ) {
        return <UploadRatesState summary={summary} />;
      }
    } else if (
      role === userRole.procurementRepresentative &&
      enableInternalCapacityMatching
    ) {
      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Pending */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.pricing &&
            x.capacityMatchingStatus === capacityMatchingStatus.pending
        )
      ) {
        return <ProcurementViewPendingState summary={summary} role={role} />;
      }

      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Rejected */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.pricing &&
            x.capacityMatchingStatus === capacityMatchingStatus.rejected
        )
      ) {
        return <ProcurementViewRejectedState />;
      }

      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is In progress or Review*/
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.pricing &&
            (x.capacityMatchingStatus === capacityMatchingStatus.inProgress ||
              x.capacityMatchingStatus === capacityMatchingStatus.review)
        )
      ) {
        return <ProcurementViewInProgressState summary={summary} role={role} />;
      }
    } else if (
      role === userRole.carrierRelationsRepresentative &&
      enableInternalCapacityMatching
    ) {
      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is In progress */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.pricing &&
            x.capacityMatchingStatus === capacityMatchingStatus.inProgress
        )
      ) {
        return <CarrierRepViewInProgressState summary={summary} role={role} />;
      }

      /* when TL-specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Review */
      if (
        modes.some(
          x =>
            x.mode === mode.tl &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === true &&
            x.bidOwnerRepType === repType.pricing &&
            x.capacityMatchingStatus === capacityMatchingStatus.review
        )
      ) {
        return <CarrierRepViewReviewState summary={summary} role={role} />;
      }
    } else {
      const userMode = getModeForUserRole(role);

      /* when user specific-mode within the bid is in the Pending state */
      if (
        modes.some(x => x.mode === userMode && x.status === bidStatus.pending)
      ) {
        return (
          <PricingViewPendingBidState
            summary={summary}
            role={role}
            mode={userMode}
          />
        );
      }

      /* when user specific-mode within the bid is in Rejected state */
      if (
        modes.some(x => x.mode === userMode && x.status === bidStatus.rejected)
      ) {
        return <PricingViewRejectedBidState />;
      }

      /* when user specific-mode within the bid is In Progress state and IsLaneUploadComplete = false */
      if (
        modes.some(
          x =>
            x.mode === userMode &&
            x.status === bidStatus.inProgress &&
            x.isLaneUploadComplete === false &&
            (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl)
        )
      ) {
        return (
          <UploadLanesState bidId={bidId} role={role} division={division} />
        );
      }

      if (enableInternalCapacityMatching) {
        /* when TL-specific-mode within the Coyote Fleet bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Pending and user is Asset based pricing */
        if (
          modes.some(
            x =>
              x.mode === mode.tl &&
              x.status === bidStatus.inProgress &&
              x.isLaneUploadComplete === true &&
              x.bidOwnerRepType === repType.pricing &&
              x.capacityMatchingStatus === capacityMatchingStatus.pending
          ) &&
          bidTypeId ===
            bidTypes.find(x => x.label === messages.coyoteFleet.defaultMessage)
              .value &&
          role === userRole.assetBasedPricingUser
        ) {
          return <ProcurementViewPendingState summary={summary} role={role} />;
        }

        /* when TL-specific-mode within the Coyote Fleet bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is In progress or Review and user is Asset based pricing*/
        if (
          modes.some(
            x =>
              x.mode === mode.tl &&
              x.status === bidStatus.inProgress &&
              x.isLaneUploadComplete === true &&
              x.bidOwnerRepType === repType.pricing &&
              (x.capacityMatchingStatus === capacityMatchingStatus.inProgress ||
                x.capacityMatchingStatus === capacityMatchingStatus.review)
          ) &&
          bidTypeId ===
            bidTypes.find(x => x.label === messages.coyoteFleet.defaultMessage)
              .value &&
          role === userRole.assetBasedPricingUser
        ) {
          return (
            <ProcurementViewInProgressState summary={summary} role={role} />
          );
        }

        /* when user specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Pending or In Progress or Review */
        if (
          modes.some(
            x =>
              x.mode === userMode &&
              x.status === bidStatus.inProgress &&
              x.isLaneUploadComplete === true &&
              (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl) &&
              (x.capacityMatchingStatus === capacityMatchingStatus.pending ||
                x.capacityMatchingStatus ===
                  capacityMatchingStatus.inProgress ||
                x.capacityMatchingStatus === capacityMatchingStatus.review)
          )
        ) {
          return (
            <PendingProcurementReviewState
              summary={summary}
              division={division}
            />
          );
        }

        /* when user specific-mode within the bid is In Progress state and isLaneUploadComplete = true and capacityMatchingStatus is Undefined/Submitted/Rejected */
        if (
          modes.some(
            x =>
              x.mode === userMode &&
              x.status === bidStatus.inProgress &&
              x.isLaneUploadComplete === true &&
              (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl) &&
              (x.capacityMatchingStatus === capacityMatchingStatus.undefined ||
                x.capacityMatchingStatus === capacityMatchingStatus.submitted ||
                x.capacityMatchingStatus === capacityMatchingStatus.rejected)
          )
        ) {
          return (
            <SubmitPricingState
              summary={summary}
              division={division}
              role={role}
            />
          );
        }
      } else {
        /* when user specific-mode within the bid is In Progress state and isLaneUploadComplete = true */
        if (
          modes.some(
            x =>
              x.mode === userMode &&
              x.status === bidStatus.inProgress &&
              x.isLaneUploadComplete === true &&
              (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl)
          )
        ) {
          return (
            <SubmitPricingState
              summary={summary}
              division={division}
              role={role}
            />
          );
        }
      }

      /* when user specific-mode within the bid is in Feedback state */
      if (
        modes.some(
          x =>
            x.mode === userMode &&
            x.status === bidStatus.feedback &&
            (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl)
        )
      ) {
        const isAwardImportComplete = modes.find(
          x => x.mode === userMode
        ).isAwardImportComplete;
        return (
          <ImportAwardState
            bidId={bidId}
            user={user}
            isAwardImportComplete={isAwardImportComplete}
            summary={summary}
          />
        );
      }

      /* when user specific mode within the bid is in Closed state and Award status is Won */
      if (
        modes.some(
          x =>
            x.mode === userMode &&
            x.status === bidStatus.closed &&
            x.awardStatus === bidAwardStatus.won &&
            (x.bidOwnerRepType === repType.pricing || x.mode === mode.tl)
        )
      ) {
        return <UploadRatesState summary={summary} />;
      }
    }
    hideBanner();
  };

  return <Fragment>{renderBannerInfo()}</Fragment>;
};

export default BannerInfo;
